import React, { useState, ChangeEvent } from 'react';
import { StyledDateInput } from './DateInput.styles';
import { validateMMDDYYYY, parseMMDDYYYYToISO } from 'services/dateService';

interface DateInputProps {
  label?: string;
  name?: string;
  placeholder?: string;
  value?: string;
  onChange: (value: any) => void;
  sx?: Record<any, any>;
  error?: boolean;
  dark?: boolean;
  required?: boolean;
  disabled?: boolean;
}

const formatDisplayValue = (isoValue: string): string => {
  if (!isoValue) return '';

  try {
    if (isoValue.includes('T') || isoValue.includes('-')) {
      const date = new Date(isoValue);
      if (isNaN(date.getTime())) return '';

      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${month}-${day}-${year}`;
    }

    return isoValue;
  } catch (e) {
    return '';
  }
};


const DateInput: React.FC<DateInputProps> = ({
  label = 'Date',
  name,
  placeholder = 'MM-DD-YYYY',
  value,
  onChange,
  sx,
  error = false,
  dark = false,
  required = false,
  disabled = false,
}) => {

  const [displayValue, setDisplayValue] = useState<string>(formatDisplayValue(value || ""));

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    const digitsOnly = input.replace(/[^0-9-]/g, '');

    let formatted = digitsOnly;

    if (digitsOnly.length > 0) {
      const digits = digitsOnly.replace(/-/g, '');

      if (digits.length <= 2) {
        formatted = digits;
      } else if (digits.length <= 4) {
        formatted = `${digits.substring(0, 2)}-${digits.substring(2)}`;
      } else {
        formatted = `${digits.substring(0, 2)}-${digits.substring(2, 4)}-${digits.substring(4, 8)}`;
      }
    }

    setDisplayValue(formatted);

    if (validateMMDDYYYY(formatted)) {
      const isoDate = parseMMDDYYYYToISO(formatted);

      onChange(isoDate);
    } else if (formatted === '') {
      onChange('');
    } else {
      // If invalid but not empty, still update the display value
      // but don't update the parent's state with invalid data
      onChange(formatted);
    }
  };

  return (
    <StyledDateInput
      dark={dark}
      label={label}
      name={name}
      placeholder={placeholder}
      value={displayValue}
      onChange={handleInputChange}
      sx={sx}
      error={error}
      required={required}
      disabled={disabled}
    />
  );
};

export default DateInput;
